import { useCallback, useEffect, useState } from 'react';
import { WeatherLocationFavouriteItem } from '@deckee/api-app-main';
import { telemetry } from '../../../../services/backend';
import {
  buildWeatherLocationFavouriteAnalyticsPayload,
  trackWeatherLocationFavouriteAdded,
  trackWeatherLocationFavouriteRemoved,
  WeatherLocationFavouriteSource,
} from '../helpers/analytics/weatherLocationFavouriteAnalytics';
import {
  addWeatherLocationFavourite,
  deleteWeatherLocationFavourite,
  getWeatherLocationFavourites,
} from '../helpers/location/favourites';

interface WeatherLocationFavouriteAnalyticsOptions {
  source: WeatherLocationFavouriteSource;
}

export const useWeatherLocationFavourites = () => {
  const [favourites, setFavourites] = useState<WeatherLocationFavouriteItem[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const loadFavourites = useCallback(async () => {
    setIsLoading(true);
    setLoadError(false);

    try {
      const loadedFavourites = await getWeatherLocationFavourites();
      setFavourites(loadedFavourites);
    } catch (error) {
      telemetry(
        telemetry.ERROR,
        `[useWeatherLocationFavourites.loadFavourites] ${JSON.stringify(error)}`,
      );
      setFavourites([]);
      setLoadError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFavourites();
  }, [loadFavourites]);

  const removeFavourite = useCallback(
    async (
      id: string,
      analytics?: WeatherLocationFavouriteAnalyticsOptions,
    ) => {
      const favouriteToRemove = favourites.find(
        (favourite) => String(favourite.id) === id,
      );

      try {
        await deleteWeatherLocationFavourite(id);
        setFavourites((current) =>
          current.filter((favourite) => String(favourite.id) !== id),
        );

        if (analytics && favouriteToRemove) {
          trackWeatherLocationFavouriteRemoved(
            buildWeatherLocationFavouriteAnalyticsPayload({
              source: analytics.source,
              itemType: favouriteToRemove.itemType,
              latitude: favouriteToRemove.latitude,
              longitude: favouriteToRemove.longitude,
              markerType: favouriteToRemove.markerType,
            }),
          );
        }
      } catch (error) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherLocationFavourites.removeFavourite] ${JSON.stringify(error)}`,
        );
      }
    },
    [favourites],
  );

  const saveFavourite = useCallback(
    async (
      payload: {
        name: string;
        latitude: number;
        longitude: number;
        state?: string | null;
        region?: string | null;
        locationDescription?: string | null;
        markerType?: string | null;
        markerId?: string | null;
        placeId?: string | null;
        itemType: string;
        itemId: string;
      },
      analytics?: WeatherLocationFavouriteAnalyticsOptions,
    ) => {
      try {
        const response = await addWeatherLocationFavourite(payload);
        await loadFavourites();

        if (analytics) {
          trackWeatherLocationFavouriteAdded(
            buildWeatherLocationFavouriteAnalyticsPayload({
              source: analytics.source,
              itemType: payload.itemType,
              latitude: payload.latitude,
              longitude: payload.longitude,
              markerType: payload.markerType,
            }),
          );
        }

        return response?.id;
      } catch (error) {
        telemetry(
          telemetry.ERROR,
          `[useWeatherLocationFavourites.saveFavourite] ${JSON.stringify(error)}`,
        );
        return undefined;
      }
    },
    [loadFavourites],
  );

  return {
    favourites,
    isLoading,
    loadError,
    loadFavourites,
    removeFavourite,
    saveFavourite,
  };
};
